import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Paper,
  Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import EditIcon from '@mui/icons-material/Edit';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';
import { useAuth } from './AuthContext';
import EditBarModal from './EditBarModal';

const API_URL = process.env.REACT_APP_API_URL || '';

function BarCarousel({ bars, serverCurrentBar, viewedBar, onBarChange, onUpdate, onDelete, onCurrentBarChange, onAdd, onBarsReorder, onLogScoreClick }) {
  const { adminDisplayEnabled, isAuthenticated } = useAuth();
  const isAdmin = adminDisplayEnabled;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBar, setEditingBar] = useState(null);
  const isInternalChangeRef = useRef(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const [loadedFlags, setLoadedFlags] = useState(new Set()); // Track loaded flags
  
  // Find index of viewed bar and set it as default
  useEffect(() => {
    if (!isInternalChangeRef.current && viewedBar && bars.length > 0) {
      const index = bars.findIndex(b => b.id === viewedBar.id);
      if (index !== -1 && index !== currentIndex) {
        setCurrentIndex(index);
      }
    }
  }, [viewedBar?.id, bars, currentIndex]);

  // Reset carousel index when bars are reordered (bars array changes)
  useEffect(() => {
    if (bars.length > 0) {
      // If currentIndex is out of bounds, reset to 0
      if (currentIndex >= bars.length) {
        setCurrentIndex(0);
      } else if (viewedBar) {
        // Try to find the viewed bar's new position after reorder
        const newIndex = bars.findIndex(b => b.id === viewedBar.id);
        if (newIndex !== -1 && newIndex !== currentIndex) {
          setCurrentIndex(newIndex);
        }
      }
    }
  }, [bars.length, bars.map(b => `${b.id}-${b.order}`).join(','), viewedBar?.id, currentIndex]); // Trigger when bar order changes

  // Lazy load flag images based on current bar
  useEffect(() => {
    if (bars.length > 0 && currentIndex < bars.length) {
      const currentBar = bars[currentIndex];
      // Get current bar's order (or fall back to index)
      const currentOrder = currentBar?.order !== undefined ? currentBar.order : currentIndex;
      
      // Determine next bar's order (for preloading)
      const nextIndex = (currentIndex + 1) % bars.length;
      const nextBar = bars[nextIndex];
      const nextOrder = nextBar?.order !== undefined ? nextBar.order : nextIndex;
      
      // Load current flag, next flag, and adjacent flags
      const flagsToLoad = [
        currentOrder,  // Current bar's flag (highest priority)
        nextOrder,     // Next bar's flag (preload for smooth navigation)
        currentOrder - 1,
        currentOrder + 1,
        currentOrder - 2,
        currentOrder + 2,
      ].filter(order => order >= 0 && order <= 9 && !loadedFlags.has(order));

      // Load flags with priority: current first, then next, then adjacent
      const priorityOrder = [
        currentOrder,
        nextOrder,
        ...flagsToLoad.filter(order => order !== currentOrder && order !== nextOrder)
      ];

      priorityOrder.forEach(order => {
        if (order >= 0 && order <= 9 && !loadedFlags.has(order)) {
          const img = new Image();
          img.src = `/flag_num_${order}.png`;
          img.onload = () => {
            setLoadedFlags(prev => new Set([...prev, order]));
          };
        }
      });
    }
  }, [currentIndex, bars, loadedFlags]);

  // Update current bar when carousel index changes
  const handleIndexChange = (newIndex) => {
    setCurrentIndex(newIndex);
    if (bars.length > 0 && onBarChange) {
      const bar = bars[newIndex];
      if (bar) {
        isInternalChangeRef.current = true;
        onBarChange(bar);
        setTimeout(() => {
          isInternalChangeRef.current = false;
        }, 100);
      }
    }
  };

  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? bars.length - 1 : currentIndex - 1;
    handleIndexChange(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex === bars.length - 1 ? 0 : currentIndex + 1;
    handleIndexChange(newIndex);
  };

  const handleEdit = (bar) => {
    setEditingBar(bar);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setEditingBar(null);
  };

  const handleEditUpdate = (updatedBar) => {
    if (onUpdate) {
      onUpdate(updatedBar);
    }
    handleEditClose();
  };

  const handleAddBar = (newBar) => {
    if (onAdd) {
      onAdd(newBar);
    }
    handleEditClose();
  };

  // Swipe gesture handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  if (!bars || bars.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No bars available.
        </Typography>
      </Box>
    );
  }

  const currentBarData = bars[currentIndex];
  const isCurrentBar = serverCurrentBar && currentBarData && currentBarData.id === serverCurrentBar.id;

  return (
    <>
      <Box sx={{ position: 'relative', width: '100%', mb: 3 }}>
        {/* Current Bar Callout */}
        {isCurrentBar && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: -20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              px: 2,
              py: 0.5,
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'primary.dark',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
              CURRENT BAR
            </Typography>
          </Paper>
        )}

        {/* Carousel Container */}
        <Card
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          sx={{
            minHeight: { xs: '400px', sm: '350px', md: '300px' },
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            color: 'white',
            backdropFilter: 'blur(2px)',
            backgroundImage: currentBarData.background_image
              ? `url(${API_URL}${currentBarData.background_image})`
              : 'none',
            backgroundSize: 'cover',
            backgroundPosition:
              currentBarData.background_image &&
              (currentBarData.background_position_x !== null ||
                currentBarData.background_position_y !== null)
                ? `${currentBarData.background_position_x !== null ? currentBarData.background_position_x : 50}% ${currentBarData.background_position_y !== null ? currentBarData.background_position_y : 50}%`
                : 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            overflow: 'visible',
            touchAction: 'pan-y', // Allow vertical scrolling but handle horizontal swipes
            '&::before': currentBarData.background_image
              ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 0,
                }
              : {},
            '& > *': {
              position: 'relative',
              zIndex: 2,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, minHeight: { xs: '400px', sm: '350px', md: '300px' }, display: 'flex', flexDirection: 'column' }}>
            
            {/* Navigation Arrows */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: { xs: 4, sm: 8 },
                transform: 'translateY(-50%)',
                zIndex: 2,
              }}
            >
              <IconButton
                onClick={handlePrevious}
                size="small"
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  padding: { xs: '6px', sm: '8px' },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </IconButton>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                right: { xs: 4, sm: 8 },
                transform: 'translateY(-50%)',
                zIndex: 2,
              }}
            >
              <IconButton
                onClick={handleNext}
                size="small"
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  padding: { xs: '6px', sm: '8px' },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </IconButton>
            </Box>

            {/* Bar Info */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: { xs: '300px', sm: '250px', md: '200px' } }}>
              {isAdmin && (
                <Box 
                  display="flex" 
                  justifyContent="flex-end" 
                  alignItems="flex-start" 
                  mb={1.5} 
                  sx={{ 
                    position: 'absolute', 
                    top: { xs: 8, sm: 16 }, 
                    right: { xs: 8, sm: 16 }, 
                    zIndex: 6,
                    touchAction: 'manipulation',
                    pointerEvents: 'auto',
                  }}
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleEdit(currentBarData);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                    size="medium"
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(255, 255, 255, 0.4)',
                      padding: { xs: '12px', sm: '8px' },
                      minWidth: { xs: '48px', sm: '40px' },
                      minHeight: { xs: '48px', sm: '40px' },
                      width: { xs: '48px', sm: 'auto' },
                      height: { xs: '48px', sm: 'auto' },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        borderColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      '&:active': {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: { xs: '24px', sm: '20px' } }} />
                  </IconButton>
                </Box>
              )}

              {/* Featured Bar Card */}
              <Box sx={{ pt: 2 }}>
                <Paper
                  elevation={4}
                  sx={{
                    p: 1.5,
                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Flag with number */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '30px',
                      top: 0,
                      bottom: 0,
                      height: '100%',
                      width: '80px',
                      backgroundImage: (() => {
                        const flagOrder = currentBarData?.order !== undefined ? currentBarData.order : currentIndex;
                        return loadedFlags.has(flagOrder) ? `url(/flag_num_${flagOrder}.png)` : 'none';
                      })(),
                      backgroundSize: 'auto 100%',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'left center',
                      backgroundOrigin: 'border-box',
                      zIndex: 0,
                      opacity: (() => {
                        const flagOrder = currentBarData?.order !== undefined ? currentBarData.order : currentIndex;
                        return loadedFlags.has(flagOrder) ? 0.8 : 0;
                      })(),
                      transition: 'opacity 0.3s ease-in-out',
                      pointerEvents: 'none',
                    }}
                  />
                  <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ position: 'relative', zIndex: 1, pl: '90px' }}>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25, textShadow: '2px 2px 4px rgba(0, 0, 0, 0.85)', fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {currentBarData.name}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)', fontSize: '0.85rem' }}>
                        {currentBarData.address}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={1.5} sx={{ position: 'relative', zIndex: 1, pl: '90px' }}>
                    <Chip
                      label={`Par ${currentBarData.par}`}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: '24px',
                        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                      }}
                    />
                    {currentBarData.drink && (
                      <Chip
                        icon={<LocalBarIcon sx={{ fontSize: '14px !important' }} />}
                        label={currentBarData.drink}
                        size="small"
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontSize: '0.65rem',
                          height: '24px',
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                          '& .MuiChip-icon': {
                            color: 'white',
                          },
                        }}
                      />
                    )}
                    {isAuthenticated && onLogScoreClick && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SportsGolfIcon sx={{ fontSize: '14px' }} />}
                        onClick={onLogScoreClick}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontSize: '0.65rem',
                          height: '24px',
                          minWidth: 'auto',
                          px: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                          '& .MuiButton-startIcon': {
                            marginRight: '4px',
                            marginLeft: 0,
                          },
                        }}
                      >
                        Score
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {isAdmin && (
        <EditBarModal
          open={editModalOpen}
          onClose={handleEditClose}
          bar={editingBar}
          bars={bars}
          onUpdate={handleEditUpdate}
          onAdd={handleAddBar}
          onBarsReorder={onBarsReorder}
          serverCurrentBar={serverCurrentBar}
          onCurrentBarChange={onCurrentBarChange}
        />
      )}
    </>
  );
}

export default BarCarousel;
